import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { type AddRowInput, type CreateFMEAOutput, type DeleteRowInput, type FetchGridInput, type FetchGridOutput, type FetchGridStreamInput, type GridCell, type RowVO, type StreamEvents, type UpdateGenerateStatusInput, type UpdateRowInput } from '@/types/index';
import _, { findIndex } from 'lodash';
import type { SSEResponse, type PsHttpSSE } from '@patsnap-utils/sse'
import { GridModel } from './grid-model';
import { GridApi } from '@/infrastructure/api-functions/grid-api';
import { STAGE_FIELDS } from '@/constants';
import { PageModel } from './page-model';

export const GridService = Symbol('GridService');

export interface GridService {
    fetchGridData(input: FetchGridInput): Promise<FetchGridOutput>;
    fetchStreamGridData<T>(input: FetchGridStreamInput, streamEvents?: StreamEvents): PsHttpSSE<T>;
    getGridRenderingCell(row: Partial<RowVO>, fields: string[]): GridCell|undefined;
    updateGridModelData(row: Partial<RowVO>): Partial<RowVO>[];
    editGridData(input: UpdateRowInput): Promise<any>;
    updateGenerateStatus(input: UpdateGenerateStatusInput): Promise<any>;
    clearGridData(): Partial<RowVO>[];
    clearStageGridData(stage: number): Partial<RowVO>[];
    exportGridData(input: CreateFMEAOutput): Promise<string>;
    deleteGridData(input: DeleteRowInput): Promise<any>;
    addGridRow(input: AddRowInput): Promise<string>;
    getPrevRowData(input: RowVO): Partial<RowVO> | undefined;
    getNextRowData(input: RowVO): Partial<RowVO> | undefined;
}

@injectable()
export class GridServiceImpl implements GridService {
    constructor(
        @inject(GridApi)
        private gridApi: GridApi,
        @inject(GridModel)
        private _gridModel: GridModel,
        @inject(PageModel)
        private _pageModel: PageModel
    ) {
    }

    async fetchGridData(input: FetchGridInput): Promise<FetchGridOutput> {
        const data = await this.gridApi.fetchGridData(input)
        this._gridModel.update(() => data.row_data)
        return data;
    }

    fetchStreamGridData<T>(input: FetchGridStreamInput, streamEvents?: StreamEvents): PsHttpSSE<T> {
        return this.gridApi.fetchStreamGridData<T>(input)
            .onOpen((response: Response) => {
                console.log('==> onOpen', response)
                streamEvents?.onOpen && streamEvents.onOpen(response)
            })
            .onMessage((data: SSEResponse<T>) => {
                streamEvents?.onMessage && streamEvents.onMessage(data)
            })
            .onFinish(() => {
                console.log('==> onFinish')
                streamEvents?.onFinish && streamEvents.onFinish()
            })
            .onError((err: any) => {
                console.log('==> onError', err)
                streamEvents?.onError && streamEvents.onError(err)
            })
            .onClose(() => {
                console.log('==> onClose')
                streamEvents?.onClose && streamEvents.onClose()
            })
    }

    getGridRenderingCell(row_vo: Partial<RowVO>, fields: string[]):GridCell|undefined {
        const { row_id, ...cols } = row_vo
        if (!row_id || _.isEmpty(cols)) return
        const col = _.last(_.sortBy(_.keys(cols).filter(col => row_vo[col]), col => _.indexOf(fields, col)))
        if (!col) return
        return {
            row: row_id,
            col: col,
            value: row_vo[col],
        }
    }

    updateGridModelData(row: Partial<RowVO>): Partial<RowVO>[] {
        this._gridModel.update((data) => {
            const originRow = _.find(data, { row_id: row.row_id })
            if (!originRow) {
                data.push(row)
            } else {
                _.merge(originRow, row)
            }
            return data;
        })
        return this._gridModel.modelData
    }

    async editGridData(input: UpdateRowInput) {
        const data = await this.gridApi.updateGridData(input)
        this.updateGridModelData(input)
        return data
    }

    async updateGenerateStatus(input: UpdateGenerateStatusInput) {
        const data = await this.gridApi.updateGenerateStatus(input)
        this._pageModel.update((m) => {
            return {
                ...m,
                generate_status: input.generate_status,
            }
        })
        return data
    }

    clearGridData() {
        this._gridModel.update(() => [])
        return this._gridModel.modelData
    }

    clearStageGridData(stage: number) {
        const fields = STAGE_FIELDS[stage - 1]
        this._gridModel.update((data) => {
            data.forEach(row => {
                fields.forEach(field => {
                    delete row[field]
                })
            })
            return data
        })
        return this._gridModel.modelData
    }

    exportGridData(input: CreateFMEAOutput) {
        return this.gridApi.exportGridData(input)
    }

    async deleteGridData(input: DeleteRowInput) {
        const data = await this.gridApi.deleteGridData(input)
        this._gridModel.update((rows) => {
            rows = rows.filter(row => row.row_id && !input.row_ids.includes(row.row_id))
            return rows
        })
        return data
    }

    async addGridRow(input: AddRowInput) {
        const data = await this.gridApi.addGridRow(input)
        this._gridModel.update(m => {
            const prevIdx = findIndex(m, m => m.row_id === input.pre_row_id)
            m.splice(prevIdx + 1, 0, { row_id: data })
            return m
        })
        return data
    }

    getPrevRowData(input: RowVO): Partial<RowVO> | undefined {
        const idx = this._gridModel.modelData.findIndex(r => r.row_id === input.row_id)
        if (idx <= 0) return
        return this._gridModel.modelData[idx - 1]
    }

    getNextRowData(input: RowVO):Partial<RowVO> | undefined {
        const idx = this._gridModel.modelData.findIndex(r => r.row_id === input.row_id)
        if (idx >= this._gridModel.modelData.length - 1) return
        return this._gridModel.modelData[idx + 1]
    }
}
